import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Role = 'SUPERADMIN'|'GROUP_ADMIN'|'COMPANY_ADMIN'|'COO'|'OWNER'|'TEAM_LEAD'|'AGENT'|'STAFF'|'HR'|'OFFICE_MANAGER'|'FINANCE'|'CUSTOMER'|'FIELD';
export type User = { id:string; email:string; displayName:string; role:Role; legalEntityId:string|null; twoFactorMode:string; twoFactorEnabled:boolean; isBreakGlass:boolean; mustChangePassword:boolean; avatarAvailable:boolean; permissions:string[] };
type LoginResult = { status:'authenticated'|'password_change_required' } | { status:'mfa_required'; challenge:string };
type AuthContextValue = {
  user:User|null; loading:boolean; csrfToken:string|null;
  login:(email:string,password:string,remember:boolean)=>Promise<LoginResult>;
  verifyMfa:(challenge:string,code:string,remember:boolean)=>Promise<void>;
  logout:()=>Promise<void>; refresh:()=>Promise<User|null>;
};

const AuthContext = createContext<AuthContextValue|null>(null);

async function json<T>(url:string, init?:RequestInit):Promise<T> {
  const response = await fetch(url, { credentials:'include', ...init, headers:{ 'content-type':'application/json',...(init?.headers ?? {}) } });
  const body = response.status === 204 ? null : await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.message ?? 'Request failed') as Error & { code?:string; fields?:unknown };
    error.code = body?.code; error.fields = body?.fields; throw error;
  }
  return body as T;
}

export function AuthProvider({children}:{children:ReactNode}) {
  const [user,setUser] = useState<User|null>(null);
  const [csrfToken,setCsrfToken] = useState<string|null>(null);
  const [loading,setLoading] = useState(true);
  async function refresh() {
    try {
      const result = await json<{user:User;csrfToken:string}>('/api/v1/auth/me');
      setUser(result.user); setCsrfToken(result.csrfToken);return result.user;
    } catch { setUser(null); setCsrfToken(null);return null; }
    finally { setLoading(false); }
  }
  useEffect(() => { void refresh(); }, []);
  async function login(email:string,password:string,remember:boolean) {
    const result = await json<{status:'authenticated'|'password_change_required'|'mfa_required';csrfToken?:string;challenge?:string}>('/api/v1/auth/login', {
      method:'POST',body:JSON.stringify({email,password,remember})
    });
    if (result.status === 'mfa_required') return { status:'mfa_required',challenge:result.challenge! } as const;
    setCsrfToken(result.csrfToken!); await refresh(); return { status:result.status } as const;
  }
  async function verifyMfa(challenge:string,code:string,remember:boolean) {
    const result = await json<{csrfToken:string}>('/api/v1/auth/mfa/verify', { method:'POST',body:JSON.stringify({challenge,code,remember}) });
    setCsrfToken(result.csrfToken); await refresh();
  }
  async function logout() {
    if (csrfToken) await json('/api/v1/auth/logout', { method:'POST',headers:{'x-csrf-token':csrfToken} });
    setUser(null); setCsrfToken(null);
  }
  const value = useMemo(() => ({user,loading,csrfToken,login,verifyMfa,logout,refresh}), [user,loading,csrfToken]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
}

export async function api<T>(url:string, csrfToken?:string|null, init?:RequestInit):Promise<T> {
  return json<T>(url, { ...init,headers:{ ...(csrfToken ? {'x-csrf-token':csrfToken} : {}),...(init?.headers ?? {}) } });
}
